import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UseChatWindowBaseResult } from './useChatWindowBase';
import type { ConversationSession } from '../types/gateway-chat-types';

interface PendingFirstMessage {
  message: string;
  file: File | null;
}

export interface UseGatewayChatLifecycleOptions {
  chatBase: UseChatWindowBaseResult;
  autoLoadConversations?: boolean;
}

export interface InitializeSessionOptions {
  sessionKey: string;
  conversationNameFactory?: (sessionKey: string) => string;
  conversationMatcher?: (conversation: ConversationSession, sessionKey: string) => boolean;
}

export interface UseGatewayChatLifecycleResult {
  conversations: ConversationSession[];
  activeConversation?: ConversationSession;
  activeConversationId: string;
  isLoadingConversations: boolean;
  isCreatingConversation: boolean;
  loadConversations: () => Promise<ConversationSession[]>;
  createConversation: (name?: string) => Promise<ConversationSession>;
  renameConversation: (
    conversationId: string,
    conversationName: string
  ) => Promise<ConversationSession>;
  switchConversation: (conversationId: string) => Promise<void>;
  queuePendingFirstMessage: (payload: PendingFirstMessage) => void;
  clearPendingFirstMessage: () => void;
  initializeSession: (
    options: InitializeSessionOptions
  ) => Promise<ConversationSession | undefined>;
  isInitializingSession: boolean;
}

const defaultConversationMatcher = (
  conversation: ConversationSession,
  sessionKey: string
): boolean => {
  if (!sessionKey) {
    return false;
  }

  const normalizedKey = sessionKey.trim().toLowerCase();
  const metadataKey =
    'session_key' in conversation && typeof conversation.session_key === 'string'
      ? conversation.session_key.trim().toLowerCase()
      : undefined;

  if (metadataKey && metadataKey === normalizedKey) {
    return true;
  }

  return conversation.conversation_name.trim().toLowerCase() === normalizedKey;
};

export const useGatewayChatLifecycle = ({
  chatBase,
  autoLoadConversations = true,
}: UseGatewayChatLifecycleOptions): UseGatewayChatLifecycleResult => {
  const {
    session: { ensureSession },
    stream: { connectionStatus, sendMessage },
    conversations: {
      conversations,
      loadConversations,
      createConversation,
      renameConversation,
      switchConversation,
      activeConversationId,
      isLoadingConversations,
      isCreatingConversation,
    },
    prompt,
  } = chatBase;

  const [pendingFirstMessage, setPendingFirstMessage] = useState<PendingFirstMessage | null>(null);
  const isSendingPendingMessageRef = useRef(false);
  const [isInitializingSession, setIsInitializingSession] = useState(false);
  const initializeSessionPromiseRef = useRef<Promise<ConversationSession | undefined> | null>(null);
  const initializeSessionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!autoLoadConversations) {
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        await ensureSession();
        if (isMounted) {
          await loadConversations();
        }
      } catch (error) {
        console.error('Gateway chat initialization failed', error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [autoLoadConversations, ensureSession, loadConversations]);

  useEffect(() => {
    if (!pendingFirstMessage) {
      return;
    }

    if (connectionStatus !== 'connected' || isCreatingConversation || !activeConversationId) {
      return;
    }

    if (isSendingPendingMessageRef.current) {
      return;
    }

    const messageToSend = pendingFirstMessage;
    setPendingFirstMessage(null);
    isSendingPendingMessageRef.current = true;

    const sendPendingMessage = async () => {
      try {
        const { message, file } = messageToSend;

        const displayContent = file ? `📎 Uploaded: ${file.name}` : message;
        const promptText = message || (file ? 'Analyze this CSV file' : '');

        await sendMessage({
          prompt: promptText,
          displayContent,
          file: file ?? undefined,
        });
      } catch (error) {
        console.error('Failed to send pending message:', error);
        if (messageToSend.message) {
          prompt.onUserPromptChange(messageToSend.message);
        }
      } finally {
        isSendingPendingMessageRef.current = false;
      }
    };

    void sendPendingMessage();
  }, [
    activeConversationId,
    isCreatingConversation,
    pendingFirstMessage,
    prompt,
    sendMessage,
    connectionStatus,
  ]);

  const queuePendingFirstMessage = useCallback((payload: PendingFirstMessage) => {
    setPendingFirstMessage(payload);
  }, []);

  const clearPendingFirstMessage = useCallback(() => {
    setPendingFirstMessage(null);
  }, []);

  const initializeSession = useCallback(
    ({
      sessionKey,
      conversationNameFactory,
      conversationMatcher,
    }: InitializeSessionOptions): Promise<ConversationSession | undefined> => {
      if (!sessionKey) {
        return Promise.resolve(undefined);
      }

      if (initializeSessionPromiseRef.current && initializeSessionKeyRef.current === sessionKey) {
        return initializeSessionPromiseRef.current;
      }

      setIsInitializingSession(true);

      const initPromise = (async () => {
        try {
          await ensureSession();
          const conversationList = await loadConversations();

          const matcher = conversationMatcher ?? defaultConversationMatcher;
          const matchingConversation = conversationList.find((conversation) =>
            matcher(conversation, sessionKey)
          );

          if (matchingConversation) {
            await switchConversation(matchingConversation.conversation_id);
            return matchingConversation;
          }

          const conversationName = conversationNameFactory?.(sessionKey) ?? sessionKey;
          const createdConversation = await createConversation(conversationName);
          return createdConversation;
        } finally {
          // Avoid "used before assigned" by referencing the ref directly
          if (initializeSessionPromiseRef.current) {
            initializeSessionPromiseRef.current = null;
            initializeSessionKeyRef.current = null;
            setIsInitializingSession(false);
          }
        }
      })();

      initializeSessionPromiseRef.current = initPromise;
      initializeSessionKeyRef.current = sessionKey;

      return initPromise;
    },
    [createConversation, ensureSession, loadConversations, switchConversation]
  );

  const activeConversation = useMemo(
    () =>
      conversations.find((conversation) => conversation.conversation_id === activeConversationId),
    [activeConversationId, conversations]
  );

  return {
    conversations,
    activeConversation,
    activeConversationId,
    isLoadingConversations,
    isCreatingConversation,
    loadConversations,
    createConversation,
    renameConversation,
    switchConversation,
    queuePendingFirstMessage,
    clearPendingFirstMessage,
    initializeSession,
    isInitializingSession,
  };
};
